import {
  Component,
  ElementRef,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import {
  AbstractControl,
  FormControl,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { Router } from '@angular/router';
import { debounceTime, distinctUntilChanged, filter, Subscription } from 'rxjs';
import { selectOptionsFromEnumCapitalized } from 'src/app/common/utilities/enum-helpers';
import { ProformPreviewModalComponent } from 'src/app/private/shared/components/modals/proform-preview-modal/proform-preview-modal.component';
import { ProformsAdminService } from 'src/app/private/shared/services/proforms/proforms-admin.service';
import {
  CreateProFormItemPayload,
  UpdateProFormPayload,
} from 'src/app/private/shared/types/payloads/proforms.payloads';
import {
  ProFormsAPIResponse,
  ProFormStatus,
  ProFormType,
} from 'src/app/private/shared/types/responses/proforms.responses';

@Component({
  selector: 'app-microlearning-quiz-builder',
  standalone: false,

  templateUrl: './microlearning-quiz-builder.component.html',
  styleUrl: './microlearning-quiz-builder.component.scss',
})
export class MicrolearningQuizBuilderComponent implements OnInit, OnDestroy {
  @Input() formId: number;

  @ViewChild('outroBody') outroBody!: ElementRef;

  @ViewChild('previewModal') previewModal!: ProformPreviewModalComponent;

  subs: Subscription[] = [];

  form: ProFormsAPIResponse;

  isEditingOutro = false;

  proFormType = ProFormType;

  types: { key: string; value: string }[];

  statuses: { key: string; value: string }[];

  passingGradeControl: FormControl;

  constructor(
    public router: Router,
    private proformsAdminService: ProformsAdminService
  ) {}

  ngOnInit() {
    if (this.formId) {
      this.proformsAdminService.getProForm(this.formId);
      this.subs.push(
        this.proformsAdminService.currentProForm.subscribe((response) => {
          if (response) {
            this.form = response;
            this.initializeFormData(response);
          }
        })
      );
    }

    this.statuses = selectOptionsFromEnumCapitalized(ProFormStatus).filter(
      (status) => status.key !== 'deleted'
    );

    this.types = selectOptionsFromEnumCapitalized(ProFormType);
  }

  initializeFormData(form: ProFormsAPIResponse) {
    // Sort questions
    if (form.sections) {
      form.sections.forEach((section) => {
        if (section.questions) {
          section.questions.sort((a, b) => a.sort_order - b.sort_order);
        }
      });
    }

    // Setup passing grade control
    this.passingGradeControl = new FormControl(form.passing_grade, [
      Validators.required,
      Validators.min(0),
      Validators.max(100),
      this.wholeNumberValidator,
    ]);

    this.passingGradeControl.valueChanges
      .pipe(
        debounceTime(500),
        distinctUntilChanged(),
        filter(() => this.passingGradeControl.valid)
      )
      .subscribe(() => {
        this.updatePassingGrade();
      });

    // Set first active question
    setTimeout(() => {
      if (form.sections?.[0]?.questions?.length === 1) {
        this.proformsAdminService.setActiveQuestion(
          form.sections[0].questions[0].id
        );
      }
    }, 500);
  }

  // Close the editor when clicking outside the card-body
  // Listen for clicks anywhere in the document
  @HostListener('document:click', ['$event'])
  onClickOutside(event: MouseEvent) {
    // Check if the user is currently editing and if the click is outside the outro body
    if (
      this.isEditingOutro &&
      this.outroBody &&
      !this.outroBody.nativeElement.contains(event.target)
    ) {
      this.isEditingOutro = false;
    }
  }

  // Custom validator to disallow decimals
  // eslint-disable-next-line class-methods-use-this
  wholeNumberValidator(control: AbstractControl): ValidationErrors | null {
    const value = control.value;
    if (value == null || value === '') return null; // Let required validator handle emptiness
    return Number.isInteger(+value) ? null : { notWholeNumber: true };
  }

  updatePassingGrade() {
    this.proformsAdminService.updateProForm(this.formId, {
      passing_grade: this.passingGradeControl.value,
    });
  }

  updateShowMissed($event: Event) {
    if (this.form) {
      const inputElement = $event.target as HTMLInputElement;
      const showMissed = inputElement.checked ? 1 : 0;
      this.form.show_missed = showMissed;

      const updatePayload: UpdateProFormPayload = {
        show_missed: showMissed,
      };

      // If "Show missed" is turned OFF, also turn off "Show answers"
      if (!showMissed && this.form.show_answers) {
        this.form.show_answers = 0;
        updatePayload.show_answers = 0;
      }

      this.proformsAdminService.updateProForm(this.formId, updatePayload);
    }
  }

  updateShowAnswers($event: Event) {
    if (this.form) {
      const inputElement = $event.target as HTMLInputElement;
      this.form.show_answers = inputElement.checked ? 1 : 0;
      this.proformsAdminService.updateProForm(this.formId, {
        show_answers: this.form.show_answers,
      });
    }
  }

  updateOutro(outro: string) {
    this.proformsAdminService.updateProForm(this.formId, { outro });
  }

  showEditOutro(event: MouseEvent) {
    event.stopPropagation();
    this.isEditingOutro = true;
  }

  createQuestion(sectionId: number) {
    const payload: CreateProFormItemPayload = {
      field_type: 'radio',
      question: 'Question',
      helper_text: '',
    };
    this.proformsAdminService.createQuestion(sectionId, payload);
  }

  ngOnDestroy() {
    let sub = this.subs.pop();
    while (sub) {
      sub.unsubscribe();
      sub = this.subs.pop();
    }
  }
}
